import React, { useMemo } from "react";
import { getWowStatic } from "hooks/wow/getStaticData";
import WowTooltip from "../../Tooltips/WowTooltip";
import { TooltipContainer } from "@outplayed/tooltips";
import { ReactComponent as TooltipIcon } from "svg/question-line.svg";
import {
  getItemQualityColor,
  getAbbreviatedClass,
  getAbbreviatedSpec,
  getFormattedContentType,
  EXPANSION,
} from "../../../lib/wowHelpers";
import SubtitleAndDescription from "../../Common/SubtitleAndDescription";
import useSorting from "hooks/useSorting";
import classNames from "classnames";
import { formatEnchantName, getEnchantQualityIconSrc } from "lib/enchant-display";
import EnchantTooltip from "../../Tooltips/EnchantTooltip";

const GemEnchantContainer = (props) => {
  const { classSpecGems, classSpecEnchants, isMobile, isHealer, isTank, encounterType, displayClassName, displaySpecName } =
    props;

  const pvpSuffix = encounterType?.toLowerCase() === "pvp" ? " for PvP" : "";

  let enchantTitles = {
    // perm_belt: "Waist Enchants",
    perm_cape: "Back Enchants",
    perm_chest: "Chest Enchants",
    perm_feet: "Feet Enchants",
    // perm_gloves: "Hands Enchants",
    // perm_head: "Head Enchants",
    perm_legs: "Legs Enchants",
    perm_neck: "Neck Enchants",
    perm_ring1: "Ring One Enchants",
    perm_ring2: "Ring Two Enchants",
    // perm_shoulder: "Shoulders Enchants",
    perm_wrist: "Wrist Enchants",
    perm_weapon1: "Main Hand Enchants",
    perm_weapon2: "Off Hand Enchants",
  };

  return classSpecGems && classSpecEnchants ? (
    <div>
      <SubtitleAndDescription
        subtitle={`Best ${displaySpecName} ${displayClassName} Gems${pvpSuffix}`}
        description={`Check out the best ${getAbbreviatedSpec(displaySpecName)} ${getAbbreviatedClass(
          displayClassName,
        )} Gems in ${EXPANSION} for ${getFormattedContentType(encounterType)}`}
        className="gems-enchants-subtitles"
      />
      <div className="wow-gem-and-enchant-page">
        <GemContainer
          category={"Gems"}
          encounterType={encounterType}
          // Top 10 most popular gems
          data={classSpecGems["gem_counts"].sort((a, b) => b.count - a.count).slice(0, 10)}
        />
        <GemContainer
          category={"Gem Builds"}
          encounterType={encounterType}
          // Top 10 most popular gem combos
          data={classSpecGems["gem_combinations"].sort((a, b) => b.count - a.count).slice(0, 10)}
        />
      </div>
      <SubtitleAndDescription
        subtitle={`${getAbbreviatedSpec(displaySpecName)} ${getAbbreviatedClass(displayClassName)} Enchant Tables${pvpSuffix}`}
        description={`These tables are a comparison of each enchant for every slot in ${getFormattedContentType(encounterType)}`}
        className="gems-enchants-subtitles"
      />
      <div className="wow-items-page">
        {Object.keys(enchantTitles).map((key) => {
          const slotEnchants = classSpecEnchants["enchants"][key] ?? {};
          // Do not show slots with no data, or the offhand when the offhand
          // item id is set to 0 as that is how our back end returns data.
          if (Object.keys(slotEnchants).length === 0 || (Object.keys(slotEnchants).length === 1 && slotEnchants[""])) {
            return null;
          } else {
            return (
              <EnchantContainer
                key={key}
                category={key}
                categoryTitle={enchantTitles[key]}
                encounterType={encounterType}
                data={classSpecEnchants["enchants"]}
                isLoading={false}
              />
            );
          }
        })}
      </div>
    </div>
  ) : (
    <div>
      <div className="wow-gem-and-enchant-page">
        <GemContainer category="Gems" encounterType={encounterType} />
        <GemContainer category="Gem Builds" encounterType={encounterType} />
      </div>

      <div className="wow-items-page">
        {Object.keys(enchantTitles).map((key) => {
          return <EnchantContainer key={key} category={key} categoryTitle={enchantTitles[key]} encounterType={encounterType} />;
        })}
      </div>
    </div>
  );
};

const GemContainer = (props) => {
  const { category, encounterType, data } = props;

  const fieldNames = [
    { title: "Max Key", key: "keys" },
    { title: "Popularity", key: "count" },
  ];
  const {
    sorted: sortedData,
    setSort,
    sortClass,
    sortMetric,
  } = useSorting({
    data,
    fieldNames,
    defaultSort: "Popularity",
  });

  return (
    <div className={category.includes("Gems") ? "wow-gems-container" : "wow-gem-builds-container"}>
      <div className="wow-gem-container-header">
        <div className="wow-gem-container-header-title">{category}</div>
        {encounterType === "mythic" && (
          <div onClick={() => setSort("Max Key")} className="wow-gem-container-header-title-metric-one sortable">
            <span
              className={classNames({
                [sortClass]: sortMetric === "Max Key",
              })}
            >
              Max Key
            </span>
            <TooltipContainer tooltipInfo="The highest key that these particular Gems or Enchants were used in and completed on time.">
              <div className="tooltip-container">
                <TooltipIcon className="tooltip-icon" />
              </div>
            </TooltipContainer>
          </div>
        )}
        <div onClick={() => setSort("Popularity")} className="wow-gem-container-header-title-metric-two sortable">
          <span
            className={classNames({
              [sortClass]: sortMetric === "Popularity",
            })}
          >
            Popularity
          </span>
          <TooltipContainer
            tooltipInfo={
              <div>
                Popularity is how often these Gems or Enchants were used by players in the top rankings of Raid or Mythic+
                Dungeons.
              </div>
            }
          >
            <div className="tooltip-container">
              <TooltipIcon className="tooltip-icon" />
            </div>
          </TooltipContainer>
        </div>
      </div>
      {sortedData.length > 0
        ? Object.keys(sortedData)
            .slice(0, 10)
            .map((key, index) => {
              if (category.includes("Gems")) {
                return (
                  <SingleGemContainers
                    key={index}
                    encounterType={encounterType}
                    itemId={sortedData[key]["id"]}
                    itemQuality={sortedData[key]["quality"]}
                    metricOne={sortedData[key]["keys"]}
                    metricTwoTop={parseFloat(sortedData[key]["perc"]).toFixed(1) + "%"}
                    metricTwoBot={"(" + sortedData[key]["count"] + ")"}
                  />
                );
              } else {
                return (
                  <MultiGemContainers
                    key={index}
                    encounterType={encounterType}
                    itemId={sortedData[key]["id"]}
                    itemQuality={sortedData[key]["quality"]}
                    metricOne={sortedData[key]["keys"]}
                    metricTwoTop={parseFloat(sortedData[key]["perc"]).toFixed(1) + "%"}
                    metricTwoBot={"(" + sortedData[key]["count"] + ")"}
                  />
                );
              }
            })
        : Array.from({ length: 10 }).map((i, index) => {
            if (category.includes("Gems")) {
              return <SingleGemContainers key={index} />;
            } else {
              return <MultiGemContainers key={index} />;
            }
          })}
    </div>
  );
};

const SingleGemContainers = (props) => {
  const { encounterType, itemId, itemQuality, metricOne, metricTwoTop, metricTwoBot } = props;
  const staticData = getWowStatic("item", itemId);

  return staticData ? (
    <div className="wow-gem-sub-container">
      <div className="wow-gem-title-header">
        <div className="wow-gem-sub-container-image">
          <WowTooltip type="item" id={itemId}>
            <div>
              <img src={staticData.img} style={{ height: "34px", width: "34px" }} />
            </div>
          </WowTooltip>
        </div>
        <div
          className="wow-gem-sub-container-title"
          style={{ color: getItemQualityColor("1") }}
          // style={{ color: getItemQualityColor(itemQuality) }}
        >
          {staticData.name}
        </div>
      </div>
      {encounterType === "mythic" && <div className="wow-gem-sub-container-metric-one">{metricOne}</div>}
      <div className="wow-gem-sub-container-metric-two">
        <div className="wow-gem-sub-container-metric-two-top">{metricTwoTop}</div>
        <div className="wow-gem-sub-container-metric-two-bot">{metricTwoBot}</div>
      </div>
    </div>
  ) : (
    <div className="wow-gem-sub-container">
      <div className="wow-gem-title-header">
        <div className="wow-gem-sub-container-title"></div>
      </div>
      {encounterType === "mythic" && <div className="wow-gem-sub-container-metric-one"></div>}
      <div className="wow-gem-sub-container-metric-two">
        <div className="wow-gem-sub-container-metric-two-top"></div>
        <div className="wow-gem-sub-container-metric-two-bot"></div>
      </div>
    </div>
  );
};

const MultiGemContainers = (props) => {
  const { encounterType, itemId, itemQuality, metricOne, metricTwoTop, metricTwoBot } = props;

  let parsedIDs = itemId ? itemId.split("-") : [];

  return parsedIDs.length > 0 && parsedIDs[0] ? (
    <div className="wow-gem-sub-container">
      <div className="wow-multi-gem-sub-container-image">
        {parsedIDs.map((x, i) => {
          return <MultiGemImageToolTips key={i} parsedItemID={x}></MultiGemImageToolTips>;
        })}
      </div>
      {encounterType === "mythic" && <div className="wow-gem-sub-container-metric-one">{metricOne}</div>}
      <div className="wow-gem-sub-container-metric-two">
        <div className="wow-gem-sub-container-metric-two-top">{metricTwoTop}</div>
        <div className="wow-gem-sub-container-metric-two-bot">{metricTwoBot}</div>
      </div>
    </div>
  ) : (
    <div className="wow-gem-sub-container">
      <div className="wow-multi-gem-sub-container-image"></div>
      {encounterType === "mythic" && <div className="wow-gem-sub-container-metric-one">{metricOne}</div>}
      <div className="wow-gem-sub-container-metric-two">
        <div className="wow-gem-sub-container-metric-two-top">{metricTwoTop}</div>
        <div className="wow-gem-sub-container-metric-two-bot">{metricTwoBot}</div>
      </div>
    </div>
  );
};

const MultiGemImageToolTips = (props) => {
  const { parsedItemID } = props;
  const staticData = getWowStatic("item", parsedItemID);

  return (
    <WowTooltip type="item" id={staticData?.id}>
      <div>
        <img src={staticData?.img} style={{ width: "34px", height: "34px", marginRight: "4px" }} />
      </div>
    </WowTooltip>
  );
};

const EnchantContainer = (props) => {
  const { category, categoryTitle, encounterType, data } = props;

  const filteredData = useMemo(() => {
    let temp = [];
    // First store the attributes to arrays.
    Object.keys(data[category]).map((key) => {
      let tempKey = data[category][key];
      if (key === "") {
        // tempKey.title = "No Enchant"
        // No Enchant Present Dont add to the array.
        // We might want to add this back in in order for the stats to add up to 100.
      } else {
        tempKey.title = key;
        temp.push(tempKey);
      }
    });
    temp.sort((a, b) => b.count - a.count);
    return temp;
  }, [data]);

  const fieldNames = [
    { title: "Max Key", key: "max_mythic_keys" },
    { title: "Popularity", key: "count" },
  ];
  const {
    sorted: sortedData,
    setSort,
    sortClass,
    sortMetric,
  } = useSorting({
    data: filteredData,
    fieldNames,
    defaultSort: fieldNames[1],
  });

  return (
    <div className={"wow-item-container"}>
      <div className="wow-item-container-header">
        <div className="wow-item-container-header-title">{categoryTitle}</div>
        {encounterType === "mythic" && (
          <div onClick={() => setSort("Max Key")} className="wow-item-container-header-title-metric-one">
            <span
              className={classNames({
                [sortClass]: sortMetric === "Max Key",
              })}
            >
              Max Key
            </span>
            <TooltipContainer tooltipInfo="The highest key that these particular Gems or Enchants were used in and completed on time.">
              <div className="tooltip-container">
                <TooltipIcon className="tooltip-icon" />
              </div>
            </TooltipContainer>
          </div>
        )}
        <div onClick={() => setSort("Popularity")} className="wow-item-container-header-title-metric-two">
          <span
            className={classNames({
              [sortClass]: sortMetric === "Popularity",
            })}
          >
            Popularity
          </span>
          <TooltipContainer
            tooltipInfo={
              <div>
                Popularity is how often these Gems or Enchants were used by players in the top rankings of Raid or Mythic+
                Dungeons.
              </div>
            }
          >
            <div className="tooltip-container">
              <TooltipIcon className="tooltip-icon" />
            </div>
          </TooltipContainer>
        </div>
      </div>
      {sortedData
        ? sortedData.slice(0, 10).map((x, i) => {
            return x.title === "No Enchant" ||
              x.title
                .match(/\+[0-9]+/)?.[0]
                .split("+")
                .some((s) => {
                  return parseInt(s) && parseInt(s) > 1000;
                }) ? null : (
              <SingleEnchantContainers
                key={i}
                encounterType={encounterType}
                itemName={x.title.split("(")[0].toString()}
                enchantItemId={x.enchant_item_id}
                enchantDisplayName={x.enchant_display_name}
                enchantQuality={x.enchant_quality}
                enchantRarity={x.enchant_rarity}
                metricOne={x.max_mythic_keys}
                metricTwoTop={parseFloat(x.perc).toFixed(1) + "%"}
                metricTwoBot={"(" + x.count + ")"}
              />
            );
          })
        : Array.from({ length: 10 }).map((i) => (
            <SingleEnchantContainers encounterType={encounterType} key={i} isLoading={true} />
          ))}
    </div>
  );
};

const SingleEnchantContainers = (props) => {
  const {
    encounterType,
    itemName,
    enchantItemId,
    enchantDisplayName,
    enchantQuality,
    enchantRarity,
    metricOne,
    metricTwoTop,
    metricTwoBot,
  } = props;
  const staticData = getWowStatic("item", enchantItemId > 0 ? enchantItemId : 200054);

  return staticData ? (
    <div className="wow-item-sub-container">
      <div className={classNames("wow-enchant-image-and-title", { "narrow-width": encounterType === "mythic" })}>
        <div className="wow-item-sub-container-image">
          <EnchantTooltip itemId={enchantItemId}>
            <div>
              <img src={staticData.img} style={{ width: "30px", height: "30px" }} />
            </div>
          </EnchantTooltip>
        </div>
        <div className="wow-enchant-sub-container-title">
          <div className="wow-enchant-item-sub-container-title" style={{ color: getItemQualityColor((enchantRarity ?? 3) + 1) }}>
            {formatEnchantName(itemName, enchantDisplayName)}
          </div>
          <img
            style={
              enchantQuality == 2
                ? { width: "16px", height: "8px", marginLeft: "4px" }
                : { width: "16px", height: "12px", marginLeft: "4px" }
            }
            src={getEnchantQualityIconSrc(enchantQuality)}
          ></img>
        </div>
      </div>
      {encounterType === "mythic" && <div className="wow-item-sub-container-metric-one">{metricOne}</div>}
      <div className="wow-item-sub-container-metric-two">
        <div className="wow-item-sub-container-metric-two-top">{metricTwoTop}</div>
        <div className="wow-item-sub-container-metric-two-bot">{metricTwoBot}</div>
      </div>
    </div>
  ) : (
    <div className="wow-item-sub-container">
      <div className="wow-item-sub-container-image"></div>
      <div className="wow-enchant-sub-container-title">
        <div className="wow-enchant-item-sub-container-title"></div>
        <div className="wow-enchant-item-sub-container-title"></div>
      </div>

      {encounterType === "mythic" && <div className="wow-item-sub-container-metric-one"></div>}
      <div className="wow-item-sub-container-metric-two">
        <div className="wow-item-sub-container-metric-two-top"></div>
        <div className="wow-item-sub-container-metric-two-bot"></div>
      </div>
    </div>
  );
};

export default GemEnchantContainer;
